import { ConversationLoadingSkeleton } from "@circle-react-shared/AiConversation/thread/ConversationLoadingSkeleton";
import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import { useChatStore } from "@circle-react/hooks/chatsV2/store/useChatStore";
import { Alert } from "@circleco/compass/components/Alert";
import { sortBy } from "lodash";
import type { ReactNode } from "react";
import { useMemo } from "react";

import { t } from "@/i18n-js/instance";
import { useIsAvatarsV2Ui } from "@/react/components/Avatars/useIsAvatarsV2Ui";
import { didAiPauseChange } from "@/react/components/SettingsApp/CommunityInbox/BotChatRoom/ChatWindow/helper";
import { PreviewPausedIndicator } from "@/react/components/SettingsApp/CommunityInbox/BotChatRoom/ChatWindow/PreviewPausedIndicator";
import type { TiptapContent } from "@/react/components/SettingsApp/CommunityInbox/BotChatRoom/MessageBox";
import { useAvatarReplyStream } from "@/react/components/SettingsApp/CommunityInbox/hooks/useAvatarReplyStream";
import { useCommunityChat } from "@/react/components/SettingsApp/CommunityInbox/hooks/useCommunityChat";
import { useMessageList } from "@/react/components/SettingsApp/CommunityInbox/hooks/useCommunityMessageList";
import { selectInboxMessages } from "@/react/hooks/chatsV2/store/selectors";

import { AvatarComposer } from "./AvatarComposer";
import { AvatarConversationEmptyState } from "./AvatarConversationEmptyState";
import { AvatarConversationRuntime } from "./AvatarConversationRuntime";
import { AvatarPreviewBanner } from "./AvatarPreviewBanner";
import { AvatarThread } from "./AvatarThread";

interface AvatarConversationViewProps {
  avatarEditor?: ReactNode;
  bioEditor?: ReactNode;
  id: number;
  isDms: boolean;
  isPreviewMode?: boolean;
  nameEditor?: ReactNode;
  onRestart: () => void;
  suggestionsEditor?: ReactNode;
}

export const AvatarConversationView = ({
  avatarEditor,
  bioEditor,
  id,
  isDms,
  isPreviewMode,
  nameEditor,
  onRestart,
  suggestionsEditor,
}: AvatarConversationViewProps) => {
  const { createMessage, isMessagesLoading } = useMessageList({ id, isDms });
  const { previewChat, setIsProcessing, setPreviewChatProcessing } =
    useCommunityInboxStore();
  const { isChatLoading, currentChat } = useCommunityChat(id, isDms);

  // Preview updates go to previewChat, not memberChats, so currentChat goes
  // stale in preview.
  const processingChat = isPreviewMode ? previewChat : currentChat;
  useAvatarReplyStream(processingChat, isDms);
  const isAvatarsV2Ui = useIsAvatarsV2Ui(processingChat);

  const messages = useChatStore(selectInboxMessages);
  const sortedMessages = useMemo(
    () => (messages?.length ? sortBy(messages, "created_at") : []),
    [messages],
  );

  const isLoading = isChatLoading || isMessagesLoading;
  const isProcessing = !!processingChat?.is_processing;
  const setProcessing = isPreviewMode
    ? setPreviewChatProcessing
    : (isChatProcessing: boolean) =>
        setIsProcessing(isChatProcessing, "member");
  const lastMessage = sortedMessages[sortedMessages.length - 1];
  const isLastMessageFromMember =
    lastMessage?.author_type === "community_member";
  // Avoids AUI rendering its running placeholder ahead of the member's own message.
  const isAwaitingReply = isProcessing && isLastMessageFromMember;
  const agentProfile =
    previewChat?.community_bot_profile || currentChat?.community_bot_profile;
  const agentName = agentProfile?.name || t("community_inbox.agent");
  const hasMessages = sortedMessages.length > 0;
  const shouldShowPreviewBanner = isPreviewMode && hasMessages;
  // A handoff pauses the chat, so the Avatar would not answer anything sent afterwards.
  const isHandedOffPreview = isPreviewMode && !!previewChat?.ai_snoozed;
  const isChatAiSnoozed = !!processingChat?.ai_snoozed;
  // A pause or resume after the last message has no message to carry its divider.
  const hasTrailingAiPauseChange =
    !isPreviewMode &&
    didAiPauseChange(!!lastMessage?.ai_snoozed, isChatAiSnoozed);
  const isWidgetOff = agentProfile?.allow_widget_conversations === false;
  const isUnavailable =
    !isPreviewMode && (!!currentChat?.disabled || isWidgetOff);

  const onCreateMessage = async (formValues: TiptapContent) => {
    try {
      // A paused chat starts no reply, so there is nothing to wait for.
      if (!isChatAiSnoozed) setProcessing(true);
      await createMessage({ formValues, botChatRoomId: id });
      // No success-path reset: is_processing flips via the websocket update.
    } catch (error) {
      console.error("Error while creating message", error);
      setProcessing(false);
    }
  };

  if (isLoading) {
    return <ConversationLoadingSkeleton />;
  }

  const composer = isHandedOffPreview ? (
    <PreviewPausedIndicator
      onRestart={onRestart}
      description={t("community_inbox.preview_handed_off_description")}
    />
  ) : (
    <AvatarComposer
      onSubmit={onCreateMessage}
      isAttachmentsEnabled={isAvatarsV2Ui}
      isProcessing={isProcessing}
      isUnavailable={isUnavailable}
      agentName={agentName}
      statusSlot={
        <>
          {isUnavailable && (
            <Alert
              className="mb-4"
              data-testid="avatar-unavailable-notice"
              description={t("avatars.conversation.unavailable_notice", {
                agent_name: agentName,
              })}
              icon="circle-info"
              iconColor="primary"
              variant="primary"
            />
          )}
          {shouldShowPreviewBanner && (
            <AvatarPreviewBanner onRestart={onRestart} />
          )}
        </>
      }
    />
  );

  return (
    <div className="flex size-full min-h-0 flex-col">
      <AvatarConversationRuntime
        messages={sortedMessages}
        isProcessing={isAwaitingReply}
      >
        {hasMessages && (
          <>
            <div className="flex min-h-0 flex-1 flex-col">
              <AvatarThread
                trailingAiPauseChange={
                  hasTrailingAiPauseChange ? isChatAiSnoozed : undefined
                }
              />
            </div>
            <div className="mx-auto w-full max-w-2xl shrink-0 px-4 pb-5 pt-2">
              {composer}
            </div>
          </>
        )}
        {!hasMessages && (
          <AvatarConversationEmptyState
            agentName={agentName}
            agentProfile={agentProfile}
            avatarEditor={avatarEditor}
            bioEditor={bioEditor}
            composer={composer}
            nameEditor={nameEditor}
            suggestionsEditor={suggestionsEditor}
          />
        )}
      </AvatarConversationRuntime>
    </div>
  );
};
